const nargs = /\{([0-9a-zA-Z_]+)\}/g;

const format = (string, args = {}) => string.replace(nargs, (match, i, index) => {
  if (string[index - 1] === '{' && string[index + match.length] === '}') {
    return i;
  }
  return args[i] ? args[i] : '';
});

function apiFactory({ makeRequest }) {
  const apiCommonMethods = [
    ['getAnnouncement', '/announcements', 'GET'],
    ['createAnnouncement', '/announcements', 'POST'],
    ['authPassword', '/oauth/token', 'POST'],
    ['resetPassword', '/users/password.json', 'POST'],
    ['confirmPassword', '/users/password.json', 'PATCH'],
    ['confirmEmail', '/users/confirmation', 'GET'],
    ['createUser', '/users.json', 'POST'],
    ['invitation', '/users/invitation.json', 'PUT'],
    ['connectWithProvider', '/users/auth/{provider}/callback', 'POST'],
    ['getEmail', '/emails', 'GET'],
    ['upgradeAccount', '/users/{userId}/{endpoint}', 'POST'],
    ['getUpgradeAccountPlan', '/users/{userId}/upgrade', 'GET'],

    ['getProfile', '/users/me', 'GET'],
    ['getLibrary', '/users/{userId}/collections/library', 'GET'],

    ['getClients', '/users/{userId}/collections/clients', 'GET'],
    ['getArchivedClients', '/users/{userId}/collections/clients/archived', 'GET'],

    ['archiveClient', '/clients/{clientId}/archive', 'PATCH'],
    ['restoreClient', '/clients/{clientId}/unarchive', 'PATCH'],

    ['moveToGroup', '/client_groups/{groupId}/members/{clientId}', 'POST'],
    ['deleteFromGroup', '/client_groups/{groupId}/members/{clientId}', 'DELETE'],
    ['getGroupAssignment', '/client_groups/{groupId}/assignments/{type}{status}', 'GET'],

    ['createAssignment', '/{type}/{id}/assignments/{endpoint}', 'POST'],
    ['deleteAssignment', '/{type}/{id}/assignments/{endpoint}', 'DELETE'],
    ['archiveAssignment', '/{type}/{id}/assignments/archive', 'PATCH'],
    ['restoreAssignment', '/{type}/{id}/assignments/unarchive', 'PATCH'],

    ['getPersonalEntity', '/users/{userId}/collections/{type}', 'GET'],
    ['getArchivedPersonalEntity', '/users/{userId}/collections/{type}/archived', 'GET'],

    ['getClientGroups', '/users/{userId}/collections/client_groups', 'GET'],
    ['getPersonalProperties', '/users/{userId}/collections/personal_properties', 'GET'],
    ['getPersonalWorkouts', '/users/{userId}/collections/personal_workouts', 'GET'],
    ['getWorkoutEvents', '/users/{userId}/collections/workout_events', 'GET'],
    ['getWorkoutEvent', '/workout_events/{id}', 'GET'],
    ['getPros', '/users/{userId}/collections/pros', 'GET'],
    ['postPros', '/pros', 'POST'],

    ['getFullWorkoutEvent', '/workout_events/{id}/full', 'GET'],
    ['getClientsPerformance', '/clients_performance', 'GET'],
    ['getDashboards', '/dashboards/{type}', 'GET'],
    ['dismissAnnouncement', '/dashboards/client/remove_announcement', 'DELETE'],

    ['createFolder', '/folders', 'POST'],
    ['updateFolder', '/folders/{id}', 'PATCH'],

    ['uploadGroupAvatar', '/client_groups/{groupId}/avatar', 'PATCH'],
    ['uploadClientAvatar', '/user_profile/{clientProfileId}/avatar', 'PATCH'],

    ['createEntity', '/{type}', 'POST'],
    ['getEntity', '/{type}/{id}', 'GET'],
    ['updateEntity', '/{type}/{id}', 'PATCH'],
    ['deleteEntity', '/{type}/{id}', 'DELETE'],
    ['getEntityInternals', '/{type}/{id}/{endpoint}', 'GET'],

    ['duplicateEntity', '/{type}/duplicate', 'POST'],
    ['duplicateEntityItem', '/{type}/{id}/duplicate', 'POST'],
    ['createEntityItems', '/{type}/{id}/items', 'POST'],
    ['archiveClientEntity', '/{type}/{id}/archive', 'PATCH'],
    ['restoreClientEntity', '/{type}/{id}/unarchive', 'PATCH'],

    ['saveAsTemplate', '/{type}/{id}/save_as_template', 'POST'],

    ['inviteUser', '/users/{userId}/invite', 'POST'],

    ['getProductOrPlanBySlug', '/{type}/by_slug/{slug}', 'GET'],
    ['getProductPlansByNamespace', '/product_plans', 'GET'],

    ['getVideos', '/videos', 'GET'],
    ['uploadVideo', '/videos', 'POST'],
    ['searchVideo', '/videos/search', 'GET'],

    ['searchQuery', '/search', 'GET'],

    ['getSubscriptionPlans', '/subscription_plans', 'GET'],
    ['getCustomer', '/users/{userId}/customer', 'GET'],
    ['setSubscription', '/subscriptions', 'POST'],
    ['updateSubscription', '/subscriptions/{id}', 'PATCH'],
    ['cancelSubscription', '/subscriptions/{id}', 'DELETE'],
    ['setDefaultCard', '/cards/{cardId}/default', 'PATCH'],
    ['addCard', '/cards/{token}', 'PATCH'],
    ['deleteCard', '/cards/{cardId}', 'DELETE'],
    ['getUserTransactions', '/users/{userId}/collections/transactions', 'GET'],
    ['getLastFailedInvoice', '/subscriptions/{subscriptionId}/last_failed_invoice', 'GET'],
    ['paymentAttempt', '/subscriptions/{subscriptionId}/last_failed_invoice/{invoiceID}', 'PATCH'],

    ['getInvoices', '/invoices', 'GET'],
    ['createInvoice', '/stripe/invoices', 'POST'],
    ['getPaymentMethods', '/stripe/payment_methods', 'GET'],
    ['createPaymentMethod', '/stripe/payment_methods', 'POST'],
    ['deletePaymentMethod', '/stripe/payment_methods/{id}', 'DELETE'],
    ['defaultPaymentMethod', '/stripe/payment_methods/{id}/default', 'PATCH'],

    ['getPaymentIntents', '/stripe/payment_intents', 'GET'],
    ['setupPaymentIntent', '/stripe/payment_intents/setup', 'POST'],
    ['createPaymentIntent', '/stripe/payment_intents', 'POST'],
    ['finishPaymentIntent', '/stripe/payment_intents/{id}', 'PATCH'],
    ['stripeConnect', '/users/auth/stripe_connect/callback', 'GET'],

    ['getConversations', '/conversations/{type}', 'GET'],
    ['markMessageAsRead', '/messages/{id}/read', 'PATCH'],

    ['getNamespace', '/user_namespaces/{symbol}', 'GET'],
  ];

  let apiUrl = '';
  let mockMethods = {};
  let accessToken = null;
  let additionalParams = {};

  function buildQueryUrl(
    endpointUrl,
    params,
  ) {
    if (!apiUrl) {
      throw new Error('No apiUrl!');
    }
    const url = new URL(`${apiUrl}${format(endpointUrl, { ...params })}`);
    if (params.qs) url.search = new URLSearchParams(params.qs);
    return url;
  }

  function performFetch({
    endpointUrl,
    method,
    methodName,
    params,
  }) {
    const url = buildQueryUrl(
      endpointUrl,
      params,
    );

    if (mockMethods[methodName]) {
      return mockMethods[methodName]({
        url,
        endpointUrl,
        params,
      });
    }

    return makeRequest(
      {
        url,
        method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(
            accessToken
              ? ({
                Authorization: `Bearer ${accessToken}`,
              }) : {}
          ),
        },
        ...(params.body ? {
          data: JSON.stringify(params.body),
        } : {}),
        ...(params.requestParams ? params.requestParams : {}),
      },
    );
  }

  return ({
    getApiUrl() {
      return apiUrl;
    },

    setApiUrl(url) {
      apiUrl = url;
      return apiUrl;
    },

    getAccessToken() {
      return accessToken;
    },

    setAccessToken(token) {
      accessToken = token;
    },

    setMockMethods(
      mockObject,
      merge = true,
    ) {
      if (merge) {
        mockMethods = {
          ...mockMethods,
          ...mockObject,
        };
      } else {
        mockMethods = mockObject;
      }
    },

    clearMockMethods() {
      mockMethods = {};
      return mockMethods;
    },

    setAdditionalParams(
      params,
      merge = true,
    ) {
      if (merge) {
        additionalParams = {
          ...additionalParams,
          ...params,
        };
      } else {
        additionalParams = params;
      }
    },

    clearAdditionalParams() {
      additionalParams = {};
      return additionalParams;
    },

    ...(apiCommonMethods.reduce((
      acc,
      [
        methodName,
        endpointUrl,
        method,
      ],
    ) => ({
      ...acc,
      [methodName](params = {}) {
        return performFetch({
          method,
          methodName,
          endpointUrl,
          params: {
            ...additionalParams,
            ...params,
          },
        });
      },
    }), {})),
  });
}

export default apiFactory;
